import type { InputHTMLAttributes, ReactNode, Ref } from 'react';
import { forwardRef, useId } from 'react';
import { useIntl } from 'react-intl';

import { generateDataTestId } from '@/lib/tests';
import { formMessages } from '@/messages/forms';

import * as S from './Input.styles';

export type InputColorVariant =
  | 'default'
  | 'defaultInverted'
  | 'white'
  | 'grey'
  | 'transparent'
  | 'primaryLight'
  | 'lightGrey'
  | 'secondaryLightGrey';

export type InputVariant = 'boxed' | 'boxedBold' | 'underlined' | 'uncovered';

export type InputBorderColorVariant = 'default' | 'lightGray';

export type ErrorVariant = 'default' | 'full';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  borderColorVariant?: InputBorderColorVariant;
  colorVariant?: InputColorVariant;
  errorVariant?: ErrorVariant;
  hasError?: boolean;
  label?: ReactNode;
  prefix?: string;
  variant?: InputVariant;
}

const generateAriaLabel = (ariaLabel: string) => {
  if (!ariaLabel) {
    return {};
  }

  return {
    'aria-label': ariaLabel,
  };
};

export const Input = forwardRef(
  (
    {
      colorVariant = 'white',
      disabled = false,
      errorVariant = 'default',
      hasError = false,
      id: providedId,
      label,
      prefix,
      variant,
      ...props
    }: InputProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    const { formatMessage } = useIntl();
    const generatedId = useId();
    const inputId = providedId ?? generatedId;

    const optionalizedLabel = (label: ReactNode) => {
      if (typeof label === 'string' && label?.includes('Optional')) {
        return label.replace('Optional', formatMessage(formMessages.optional));
      }
      return label;
    };
    return (
      <S.Wrapper
        $variant={variant}
        colorVariant={colorVariant}
        data-test-id='InputWrapper'
      >
        <S.InputContainer $hasPrefix={!!prefix}>
          {prefix && (
            <S.Prefix $error={hasError} $hasLabel={!!label} className='prefix'>
              {prefix}
            </S.Prefix>
          )}
          {label ? (
            <S.Input
              data-test-id='InputField'
              {...generateDataTestId('input', props.name || inputId)}
              ref={ref}
              $errorVariant={errorVariant}
              $variant={variant}
              disabled={disabled}
              error={hasError}
              {...props}
              id={inputId}
            />
          ) : (
            <S.InputNoLabel
              data-test-id='InputField'
              {...generateDataTestId('input', props.name || inputId)}
              {...generateAriaLabel(props.placeholder || '')}
              ref={ref}
              $errorVariant={errorVariant}
              $variant={variant}
              disabled={disabled}
              error={hasError}
              {...props}
              id={inputId}
            />
          )}

          {label ? (
            <S.Label data-test-id='InputLabel' htmlFor={inputId}>
              {optionalizedLabel(label)}
            </S.Label>
          ) : null}
        </S.InputContainer>
      </S.Wrapper>
    );
  },
);

Input.displayName = 'Input';
